import React, { useState, useEffect } from 'react'
import { AuthProvider } from 'react-oidc-context'
import AdapterMoment from '@mui/lab/AdapterMoment'
import LocalizationProvider from '@mui/lab/LocalizationProvider'
import { useRouteMatch } from 'react-router-dom'
import { WebStorageStateStore } from 'oidc-client-ts'
import { OidcConfig, OidcConfigProvider } from './components/provider/OidcConfigProvider'
import { OIDCProviderWithSession } from './components/provider/OIDCProvider'
import { getEnvironment } from './lib/environment'
import getPublicSettings from './lib/clients/getPublicClient'
import App from './App'
import { ModalProvider, NotificationProvider, StudentApolloProvider } from './components/provider'
import { Loading } from './components/shared/loading'

const Root = () => {
  const [oidcConfig, setOidcConfig] = useState<OidcConfig | null>(null)
  const [currentAuthType, setCurrentAuthType] = useState<string | null>(null)

  // Route checks for determining authType from URL
  const civRoute = useRouteMatch({ path: '/login/civitas', exact: true })
  const loginRoute = useRouteMatch({ path: '/login', exact: true })

  useEffect(() => {
    // Determine authType from URL route if present
    let authType: any = null
    if (civRoute && civRoute.path === civRoute.url) {
      authType = 'civitas'
    }

    // Clear session storage if the current route is `/login` with no specific auth type
    if (loginRoute && loginRoute.path === loginRoute.url && !authType) {
      localStorage.removeItem('authType')
    }

    // Update session storage if a new authType is specified in the URL
    if (authType) {
      localStorage.setItem('authType', authType)
    } else {
      // Otherwise, use localStorage value if set, or fall back to settings
      authType = localStorage.getItem('authType')
    }

    const fetchConfig = async () => {
      const settings = await getPublicSettings()

      // If authType is not set, now the default will be oidc
      if (!authType) {
        authType = 'oidc'
      }

      localStorage.setItem('authType', authType) // Store this default authType

      // Only fetch configuration if authType has changed or is initially null
      if (authType !== currentAuthType) {
        setCurrentAuthType(authType) // Update the current auth type in state
        setOidcConfig(null)

        // Set a default OIDC config based on the determined authType
        let defaultConfig: OidcConfig | null = null

        // Modify config based on the determined or defaulted authType
        if (authType === 'civitas') {
          const { authSettings } = settings
          defaultConfig = {
            authority: process.env.REACT_APP_CIV_AUTHORITY || '',
            client_id: `${getEnvironment()}-oidc`,
            redirect_uri: `${window.location.origin}`,
            response_type: 'code',
            scope: `openid ${authSettings.studentAppOidcAudience}`,
            post_logout_redirect_uri: `${window.location.origin}`,
            loadUserInfo: true,
            automaticSilentRenew: true,
            userStore: new WebStorageStateStore({ store: window.localStorage }),
            extraQueryParams: {
              environment: getEnvironment(),
              audience: authSettings.studentAppOidcAudience,
              kc_idp_hint: process.env.REACT_APP_CIV_KC_IDP
            }
          }
        } else {
          const { authSettings } = settings
          defaultConfig = {
            authority: authSettings.studentAppOidcAuthority,
            client_id: authSettings.studentAppOidcClientId,
            redirect_uri: `${window.location.origin}`,
            response_type: 'code',
            scope: `openid ${authSettings.studentAppOidcAudience}`,
            post_logout_redirect_uri: `${window.location.origin}`,
            loadUserInfo: true,
            automaticSilentRenew: true,
            userStore: new WebStorageStateStore({ store: window.localStorage }),
            extraQueryParams: {
              environment: getEnvironment(),
              audience: authSettings.studentAppOidcAudience,
              kc_idp_hint: authSettings.studentAppOidcDefaultIdpAlias
            }
          }
        }

        // Save the determined configuration
        setOidcConfig(defaultConfig)
      }
    }

    fetchConfig()
  }, [civRoute, loginRoute, currentAuthType])

  if (!oidcConfig) {
    return <Loading />
  }

  return (
    <OidcConfigProvider config={oidcConfig}>
      <AuthProvider {...oidcConfig}>
        <OIDCProviderWithSession>
          <StudentApolloProvider>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <ModalProvider>
                <NotificationProvider>
                  <App />
                </NotificationProvider>
              </ModalProvider>
            </LocalizationProvider>
          </StudentApolloProvider>
        </OIDCProviderWithSession>
      </AuthProvider>
    </OidcConfigProvider>
  )
}

export default Root
